// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext, useEffect, useMemo } from 'react';
import { ReportTemplateOutput, PermissionsEnum, ObjectTypeV1, ReportTemplateInput } from 'thehive-sdk';
import client from '@Client';
import RouteBuilder from '@Services/RouteBuilder';
import { THLayout } from '@Molecules/THLayout';
import { ICONS } from '@Constants/iconsApp';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { computeEventList } from '@Organisms/ReportTemplate/eventList';
import { useLocalMessage } from '@Helpers/locale';
import { openMessage } from '@Helpers/notification';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { useSplitPermissions } from '@Hooks/useSplitPermissions';
import { useCurrentUser } from '@Queries/userQueries';
import useConfigList from '@Hooks/useConfigList';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import useDisplayParams from '@Molecules/DisplayParams/DisplayParams';
import { StatusContext } from '@Context/StatusProvider';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { FormatMultipleDate } from '@Molecules/Format';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { THList } from '@Molecules/THList';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { ReportTemplateCreate } from '@Organisms/ReportTemplate/ReportTemplateCreate/ReportTemplateCreate';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { useListAllFields } from '@Queries/describeQueries';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { useHistory, useParams } from 'react-router-dom';
import { RouteType } from '@Types';

import './ReportTemplateList.scss';

export const ReportTemplatesList = (): ReactElement => {
    const i = useLocalMessage();

    const { orgId } = useParams<{ orgId: string }>();
    const { data: currentUser } = useCurrentUser();
    const { permissions = [] } = currentUser || {};
    const { data: describe } = useListAllFields();

    const { quotas } = useContext(StatusContext);
    const history = useHistory();

    const config = useConfigList<ReportTemplateOutput>({
        selector: {
            head: [
                {
                    name: 'listCaseReportTemplate',
                },
            ],
            name: 'get-report-templates',
        },
        controller: client.ReportTemplateController,
        storageId: StorageFilterEnum.REPORT_TEMPLATE,
        objectType: ObjectTypeV1.CASE_REPORT_TEMPLATE,
    });

    const {
        dataSource = [],
        columnsRenderer,
        listRefetch,
        status = 'loading',
        countRefetch,
        filterManager,
        paginationManager,
        table,
        storageViewManager,
        storagePageManager,
    } = config;

    const { setDisplay, setOnLoadView } = storagePageManager || {};
    const { setDefaultDisplay } = storageViewManager || {};

    const [showDates, DisplayDates, loadVisibleStateDates] = useDisplayParams({
        createdAt: { label: i('common.created-date'), visible: true },
        updatedAt: { label: i('common.updated-date'), visible: true },
    });

    setOnLoadView?.((viewContent) => {
        loadVisibleStateDates({
            createdAt: viewContent?.display?.createdAt ?? true,
            updatedAt: viewContent?.display?.updatedAt ?? true,
        });
    });

    const { casereporttemplate: hasPermissions } = useSplitPermissions(permissions, [
        PermissionsEnum.MANAGE_CASE_REPORT_TEMPLATE,
    ]);

    const columns = columnsRenderer([
        {
            title: [{ label: i('common.title'), sortField: 'title' }],
            className: 'report-template-list__column__title',
            render: ({ title, _id }) => (
                <div
                    className="report-template-list__title"
                    onClick={() => {
                        if (hasPermissions) {
                            const path = RouteBuilder.compute('organisation.templates-report-details', {
                                reportTemplateId: _id,
                                orgId,
                            });

                            history.push(path);
                        }
                    }}
                >
                    {title}
                </div>
            ),
        },
        {
            title: i('general.by'),
            className: 'report-template-list__column__by',
            render: ({ _createdBy }): ReactElement => <AssigneeInfo assignee={_createdBy} avatarOnly />,
        },
        {
            title: [
                { label: i('common.dates') },
                { label: 'C.', sortField: '_createdAt', hide: !showDates.createdAt },
                { label: 'U.', sortField: '_updatedAt', hide: !showDates.updatedAt },
            ],
            optionalRender: DisplayDates,
            className: 'report-template-list__column__dates',
            render: ({ _createdAt = 0, _updatedAt = 0 }, { date: filterDate }): ReactElement => (
                <FormatMultipleDate
                    dates={[
                        {
                            label: 'C.',
                            value: _createdAt,
                            onClick: (): void => filterDate(_createdAt, '_createdAt'),
                            hide: !showDates.createdAt,
                        },
                        {
                            label: 'U.',
                            value: _updatedAt,
                            onClick: (): void => filterDate(_updatedAt, '_updatedAt'),
                            hide: !showDates.updatedAt,
                        },
                    ]}
                />
            ),
        },
        {
            className: 'report-template-list__column__actions',
            render: (reportTemplate) => (
                <>
                    {hasPermissions && (
                        <ActionsList
                            actionList={{
                                edit: {
                                    label: i('general.edit'),
                                    icon: <ICONS.EditIcon />,
                                    CustomComponent: (triggerElem) => (
                                        <ReportTemplateCreate
                                            triggerElem={triggerElem}
                                            defaultValues={reportTemplate}
                                            onEdit={(reportTemplate, reportTemplateId) =>
                                                eventList.onEdit(reportTemplate, reportTemplateId)
                                            }
                                        />
                                    ),
                                },
                                duplicate: {
                                    label: i('general.duplicate'),
                                    icon: <ICONS.CopyOutlined />,
                                    CustomComponent: (triggerElem) => (
                                        <ReportTemplateCreate
                                            triggerElem={triggerElem}
                                            defaultValues={reportTemplate}
                                            onEdit={(reportTemplate) => eventList.onCreate(reportTemplate)}
                                        />
                                    ),
                                },
                                delete: {
                                    label: i('general.delete'),
                                    confirm: true,
                                    message: i('report-template.actions.delete.single'),
                                    icon: <ICONS.DeleteIcon />,
                                    query: () => eventList.onDelete(reportTemplate._id),
                                },
                            }}
                            isIcon
                            scope="single"
                        />
                    )}
                </>
            ),
        },
    ]);

    useEffect(() => {
        setDisplay?.('createdAt', showDates.createdAt);
        setDisplay?.('updatedAt', showDates.updatedAt);
    }, [showDates]); // eslint-disable-line

    useEffect(() => {
        const currentView = storagePageManager?.page?.currentView || '';

        setDefaultDisplay?.(currentView, {
            createdAt: showDates.createdAt,
            updatedAt: showDates.updatedAt,
        });
    }, [storagePageManager, showDates, setDefaultDisplay]);

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.ReportTemplateController,
                locale: i,
                onSuccess: (_, message) => {
                    if (message) {
                        openMessage('success', message);
                        listRefetch();
                        countRefetch();
                    }
                },
            }),
        [i, listRefetch, countRefetch],
    );

    const handleCreateReportTemplate = async (reportTemplate: ReportTemplateInput) => {
        const newTemplate = await eventList.onCreate(reportTemplate);
        history.push(
            RouteBuilder.compute('organisation.templates-report-details', {
                reportTemplateId: (newTemplate as ReportTemplateOutput)._id,
                orgId,
            }),
        );
        return newTemplate;
    };

    return (
        <THLayout>
            <THLayout.Content>
                <THList>
                    <THList.Header>
                        <THList.Slot name="MANAGE">
                            {hasPermissions && (
                                <>
                                    <ReportTemplateCreate
                                        triggerElem={
                                            <AddButton
                                                label={i('report-template.report-list.add')}
                                                disabled={!quotas?.case?.template?.valid}
                                            />
                                        }
                                        onCreate={(reportTemplate) => handleCreateReportTemplate(reportTemplate)}
                                    />
                                </>
                            )}

                            {storagePageManager && storageViewManager ? (
                                <ManageViewList
                                    pageManager={storagePageManager}
                                    viewManager={storageViewManager}
                                    beforeCreate={(content: any): any => {
                                        filterManager.applyFilters();
                                        return { ...content, filters: filterManager.tempFilters };
                                    }}
                                />
                            ) : null}
                        </THList.Slot>
                        <THList.Slot name="NAVIGATION">
                            <ManageSwitch
                                label={i('common.filters')}
                                value={filterManager.editFilters}
                                onChange={(editFilters): void => {
                                    filterManager.setEditFilters(editFilters);
                                }}
                                icon={<ICONS.FilterOutlined />}
                            />
                        </THList.Slot>
                        <THList.Slot name="CONTENT">
                            {(filterManager.editFilters || !!filterManager.tempFilters.length) && (
                                <THFilter
                                    filterConfig={filterManager}
                                    describeField={describe?.[ObjectTypeV1.CASE_REPORT_TEMPLATE]}
                                >
                                    <Footer filterConfig={filterManager} />
                                </THFilter>
                            )}
                        </THList.Slot>
                    </THList.Header>

                    <THList.Content
                        dataSource={dataSource}
                        columns={columns}
                        rowKey="_id"
                        emptyPlaceholder={
                            <DisplayEmptyData spin={status === 'loading'} label={i('report-template.none')}>
                                {hasPermissions && (
                                    <ReportTemplateCreate
                                        triggerElem={
                                            <THButton type="link">{i('report-template.report-list.add')}</THButton>
                                        }
                                        onCreate={(reportTemplate) => handleCreateReportTemplate(reportTemplate)}
                                    />
                                )}
                            </DisplayEmptyData>
                        }
                    />

                    <THList.Footer>
                        <THList.Slot name="NAVIGATION">
                            <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                            <PaginationSize paginationConfig={paginationManager} />
                        </THList.Slot>
                    </THList.Footer>
                </THList>
            </THLayout.Content>
        </THLayout>
    );
};

export const Route: RouteType = {
    content: <ReportTemplatesList />,
    path: RouteBuilder.compute('organisation.templates-report'),
    label: 'Report Templates',
    space: ['user'],
    menu: {
        label: 'organisation.layout.nav-bar.templates',
    },
    breadcrumb: { label: 'breadcrumb.organisation.report-templates' },
};
